import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../../context/AdminAuthContext';
import config from '../../config/appConfig';
import '../../styles/Admin.css';

const NAV = [
  { section: 'Overview' },
  { to: '/admin', label: 'Dashboard',  icon: '◈', exact: true },

  { section: 'Management' },
  { to: '/admin/users',     label: 'Users',     icon: '👤' },
  { to: '/admin/providers', label: 'Providers', icon: '🏥' },

  { section: 'QR & Stock' },
  { to: '/admin/assets',     label: 'QR Codes',   icon: '▣' },
  { to: '/admin/inventory',  label: 'Inventory',  icon: '📦' },
  { to: '/admin/wholesale',  label: 'Wholesale',  icon: '🏪' },

  { section: 'System', superOnly: true },
  { to: '/admin/admins',   label: 'Administrators', icon: '🛡', superOnly: true },
  { to: '/admin/settings', label: 'Settings',       icon: '⚙', superOnly: true },

  { section: 'Personal' },
  { to: '/admin/appearance', label: 'Appearance', icon: '🎨' },
];

export default function AdminLayout({ children, title }) {
  const { admin, logout } = useAdminAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [open, setOpen] = useState(false);

  const isActive = (to, exact) =>
    exact ? location.pathname === to : location.pathname.startsWith(to);

  const handleLogout = () => { logout(); navigate('/admin/login'); };
  const initials = admin
    ? `${admin.firstName?.[0] ?? ''}${admin.lastName?.[0] ?? ''}`.toUpperCase()
    : 'A';

  return (
    <div className="admin-root">
      {/* Sidebar overlay (mobile) */}
      <div
        className={`admin-sidebar-overlay ${open ? 'open' : ''}`}
        onClick={() => setOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${open ? 'open' : ''}`}>
        <div className="admin-sidebar-logo">
          <h1>{config.appIcon} {config.appShortName} Admin</h1>
          <span>Management Console</span>
        </div>

        <nav className="admin-nav">
          {NAV.map((item, i) => {
            // Hide super-only items from non-super admins
            if (item.superOnly && admin?.role !== 'super_admin') return null;
            return item.section ? (
              <div key={i} className="admin-nav-section">{item.section}</div>
            ) : (
              <button
                key={item.to}
                className={`admin-nav-link ${isActive(item.to, item.exact) ? 'active' : ''}`}
                onClick={() => { navigate(item.to); setOpen(false); }}
              >
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="admin-sidebar-footer">
          <div className="admin-user-pill">
            <div className="admin-user-avatar">{initials}</div>
            <div className="admin-user-info">
              <div className="name">{admin?.firstName} {admin?.lastName}</div>
              <div className="role">{admin?.role?.replace('_', ' ')}</div>
            </div>
            <button className="admin-logout-btn" onClick={handleLogout} title="Sign out">⏻</button>
          </div>
        </div>
      </aside>

      {/* Main */}
      <main className="admin-main">
        <div className="admin-topbar">
          <button className="admin-menu-toggle" onClick={() => setOpen(o => !o)}>☰</button>
          <h2>{title}</h2>
          <div className="admin-topbar-actions">
            <span style={{ fontSize: 12, color: 'var(--text3)' }}>
              {admin?.email}
            </span>
          </div>
        </div>

        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
}
